/**
 * 轻图测试（LD_ASSIST）工具
 *
 * 职责：
 * 1. LightMapDraft 状态类型定义
 * 2. createLightMapPayload — 组装创建轻图测试的请求体
 *
 * 注意：新架构统一使用 GCJ02 坐标提交
 */
import {
  AnchorLayerType,
  EffectDateType,
  EffectVehicleType,
  LightMapAssistType,
} from '@/utils/constant';
import type MapEngine from '@/map-core';

export type LightMapDraftStage =
  | 'bufferDrawing'
  | 'bufferReady'
  | 'parkingDrawing'
  | 'parkingReady'
  | 'warningDrawing'
  | 'warningReady'
  | 'submitting';

export type LightMapDraftPolygon = {
  id: string;
  feature: any;
};

export type LightMapDraft = {
  stage: LightMapDraftStage;
  subType: LightMapAssistType;
  buffer?: LightMapDraftPolygon;
  parking?: LightMapDraftPolygon;
  warning?: LightMapDraftPolygon;
};

export type LightMapPairFormValues = {
  bufferRemark: string;
  parkingRemark: string;
};

export type LightMapWarningFormValues = {
  remark: string;
};

export interface LightMapPayloadInput {
  mapId: number | string;
  draft: LightMapDraft;
  getGeometry: (feature: any) => any;
  values: LightMapPairFormValues | LightMapWarningFormValues;
}

/**
 * 组装轻图测试创建 payload
 *
 * 根据 subType 区分：
 * - BUFFER_ZONE：主记录 buffer + subVariable parking
 * - WARNING_ZONE：单记录 warning
 */
export const createLightMapPayload = ({
  mapId,
  draft,
  getGeometry,
  values,
}: LightMapPayloadInput) => {
  if (draft.subType === LightMapAssistType.WARNING_ZONE) {
    if (!draft.warning) {
      throw new Error('轻图危险区未完成绘制');
    }
    return {
      mapId,
      type: AnchorLayerType.LD_ASSIST,
      subType: LightMapAssistType.WARNING_ZONE,
      effectVehicleType: EffectVehicleType.ALL_EFFECT,
      effectDateType: EffectDateType.PERMANENT,
      geometryGcj02: getGeometry(draft.warning.feature),
      remark: (values as LightMapWarningFormValues).remark,
    };
  }

  if (!draft.buffer || !draft.parking) {
    throw new Error('轻图缓冲区和停车区均需完成绘制');
  }

  const pairValues = values as LightMapPairFormValues;
  return {
    mapId,
    type: AnchorLayerType.LD_ASSIST,
    subType: LightMapAssistType.BUFFER_ZONE,
    effectVehicleType: EffectVehicleType.ALL_EFFECT,
    effectDateType: EffectDateType.PERMANENT,
    geometryGcj02: getGeometry(draft.buffer.feature),
    remark: pairValues.bufferRemark,
    subVariable: {
      type: AnchorLayerType.LD_ASSIST,
      subType: LightMapAssistType.PARKING_ZONE,
      geometryGcj02: getGeometry(draft.parking.feature),
      remark: pairValues.parkingRemark,
    },
  };
};
